import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { getPlatformIcon, getFlyerUrl } from './utils/platformUtils';
import PlatformAnalyticsCard from './PlatformAnalyticsCard';
import { openChannelPost } from '../../utils/channelUtils';

interface ChannelAnalytics {
  [key: string]: any;
}

interface CampaignAnalyticsRowProps {
  campaign: any;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  channelAnalytics: Record<string, Record<string, ChannelAnalytics>>;
  loadingAnalytics: Record<string, Set<string>>;
}

const CampaignAnalyticsRow: React.FC<CampaignAnalyticsRowProps> = ({
  campaign,
  index,
  isExpanded,
  onToggle,
  channelAnalytics,
  loadingAnalytics,
}) => {
  const publishData = campaign.publishData || campaign.publish_data || [];
  const postedChannels = publishData.filter((pd: any) => pd.status === 'posted');

  // Calculate campaign duration
  const campaignData = campaign.campaignData || campaign.campaign_data || {};
  const validFrom = campaignData.validFrom ? new Date(campaignData.validFrom) : null;
  const validTo = campaignData.validTo ? new Date(campaignData.validTo) : null;
  const durationDays = validFrom && validTo 
    ? Math.ceil((validTo.getTime() - validFrom.getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  // Get earliest posted date
  const postedDate = postedChannels.length > 0 && postedChannels[0].date
    ? new Date(postedChannels[0].date)
    : null;

  // Calculate campaign status
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = validTo ? new Date(validTo) : null;
  if (endDate) endDate.setHours(0, 0, 0, 0);
  const isCompleted = endDate && endDate < today;
  const status = isCompleted ? 'Completed' : 'In Progress';
  const statusColor = isCompleted ? 'text-green-500' : 'text-orange-500';

  const flyerUrl = getFlyerUrl(campaign);

  return (
    <React.Fragment key={campaign.id}>
      {/* Main Row */}
      <motion.tr
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: index * 0.05 }}
        onClick={onToggle}
        className="border-b border-gray-800 cursor-pointer transition-colors hover:bg-gray-900/30"
      >
        {/* Campaign Info */}
        <td className="p-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-6">
              {isExpanded ? (
                <MdKeyboardArrowUp className="text-brand-primary text-2xl" />
              ) : (
                <MdKeyboardArrowDown className="text-gray-400 text-2xl" />
              )}
            </div>
            {flyerUrl && (
              <img
                src={flyerUrl}
                alt={campaign.title}
                className="w-14 h-14 object-cover object-top rounded-lg border border-gray-700"
              />
            )}
            <div>
              <div className="text-white font-medium">{campaign.title}</div>
              {validFrom && validTo && (
                <div className="text-gray-400 text-xs mt-1">
                  {validFrom.toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                  })}{' '}
                  -{' '}
                  {validTo.toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                  })}
                </div>
              )}
            </div>
          </div>
        </td>

        {/* Platforms */}
        <td className="p-4">
          <div className="flex gap-3 items-center">
            {postedChannels.map((pd: any) => (
              <button
                key={pd.postId}
                onClick={(e) => {
                  e.stopPropagation();
                  openChannelPost({ 
                    channel: pd.channel, 
                    postId: pd.postId,
                    shortcode: pd.shortcode 
                  });
                }}
                className="cursor-pointer hover:opacity-70 transition-opacity"
                title={`View ${pd.channel} post`}
              >
                {getPlatformIcon(pd.channel)}
              </button>
            ))}
          </div>
        </td>

        {/* Posted On */}
        <td className="p-4">
          <div className="text-gray-300 text-sm">
            {postedDate
              ? `${postedDate.getDate()}-${postedDate.toLocaleDateString('en-US', {
                  month: 'short',
                })}-${postedDate.getFullYear()}`
              : '-'}
          </div>
        </td>

        {/* Status */}
        <td className="p-4">
          <div className={`text-sm font-medium ${statusColor}`}>
            {status}
          </div>
        </td>

        {/* Duration */}
        <td className="p-4 text-right">
          <div className="text-gray-300 text-sm">
            {durationDays > 0 ? `${durationDays} ${durationDays === 1 ? 'day' : 'days'}` : '-'}
          </div>
        </td>
      </motion.tr>

      {/* Expanded Detail Row */}
      <AnimatePresence>
        {isExpanded && (
          <motion.tr
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-[#000000] border-b border-gray-800"
          >
            <td colSpan={5} className="p-0">
              <div className="p-6">
                <div className="grid grid-cols-12 gap-6">
                  {/* Left: Flyer Image */}
                  {flyerUrl && (
                    <div className="col-span-2">
                      <img
                        src={flyerUrl}
                        alt={campaign.title}
                        className="w-auto h-80 rounded-xl border border-gray-700 object-cover"
                      />
                    </div>
                  )}

                  {/* Right: Campaign Details */}
                  <div className={flyerUrl ? 'col-span-10' : 'col-span-12'}>
                    {/* Campaign Header */}
                    <h2 className="text-xl font-medium text-white mb-4">
                      {campaign.title}
                    </h2>

                    {/* Date Info */}
                    {validFrom && validTo && (
                      <div className="flex items-center gap-6 text-sm mb-6">
                        <div>
                          <span className="text-gray-400">From: </span>
                          <span className="text-white text-xs">
                            {validFrom.toLocaleDateString('en-US', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-400">To: </span>
                          <span className="text-white text-xs">
                            {validTo.toLocaleDateString('en-US', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </span>
                        </div>
                        {durationDays > 0 && (
                          <div>
                            <span className="text-gray-400">Duration: </span>
                            <span className="text-white text-xs">{durationDays} days</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Platform Analytics Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      {postedChannels.map((post: any) => {
                        const analytics = channelAnalytics[campaign.id]?.[post.channel];
                        const isLoading = loadingAnalytics[campaign.id]?.has(post.channel);

                        return (
                          <PlatformAnalyticsCard
                            key={post.postId}
                            channel={post.channel}
                            postId={post.postId}
                            analytics={analytics}
                            isLoading={isLoading}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </React.Fragment>
  );
};

export default CampaignAnalyticsRow;
